import { useEffect, useRef, useState } from 'react';
import { Button, SheetGrip, TextField } from '@/components/ui';
import { api, type EmailChangeResult } from '@/lib/api';
import { useT } from '@/i18n/react';
import './otp.css';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';

/**
 * E-mail hitelesítés 6 jegyű kóddal.
 * docs/02-funkcionalis-spec.md → Onboarding, 3. lépés
 */

export interface OtpDialogProps {
  email: string;
  onVerified: () => void;
  onClose: () => void;
  /**
   * Replaces a mistyped address (`useAuth().changeEmail`). Without it the
   * "Wrong address?" option is hidden.
   */
  onChangeEmail?: (email: string) => Promise<EmailChangeResult>;
}

const LENGTH = 6;

export function OtpDialog({ email, onVerified, onClose, onChangeEmail }: OtpDialogProps) {
  const t = useT();
  // The caller's `user.email` lags behind a change — show this address instead.
  const [address, setAddress] = useState(email);
  const [changing, setChanging] = useState(false);
  const [draft, setDraft] = useState('');
  const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(''));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const code = digits.join('');
  const complete = code.length === LENGTH;

  // Első betöltéskor küldünk kódot
  /* Android vissza gomb: ez a réteg záródjon, ne a képernyő váltson. */
  useBackHandler(true, () => onClose());
  // Lefelé húzva is lecsukható (#71).
  const panelRef = useSwipeToClose<HTMLDivElement>(onClose);

  useEffect(() => {
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Újraküldési visszaszámláló
  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setInterval(() => setWait((w) => Math.max(0, w - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [wait]);

  async function send() {
    setError('');
    setBusy(true);
    try {
      const result = await api.otpSend();
      if (result.alreadyVerified) {
        onVerified();
        return;
      }
      setWait(result.waitSeconds || 60);
      setNotice(
        result.devCode
          ? t.emailVerification.otp.devCode(result.devCode)
          : t.emailVerification.otp.sent,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t.emailVerification.otp.sendFailed);
    } finally {
      setBusy(false);
    }
  }

  async function changeAddress() {
    if (!onChangeEmail) return;
    setError('');
    setBusy(true);
    try {
      const result = await onChangeEmail(draft.trim());
      setAddress(result.email);
      setChanging(false);
      setDigits(Array(LENGTH).fill(''));
      setWait(result.waitSeconds || 60);
      setNotice(
        result.devCode
          ? t.emailVerification.otp.devCode(result.devCode)
          : t.emailVerification.otp.changed,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t.emailVerification.otp.changeFailed);
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setError('');
    setBusy(true);
    try {
      await api.otpVerify(value);
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.emailVerification.otp.invalidCode);
      setDigits(Array(LENGTH).fill(''));
      inputs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  function setDigit(index: number, value: string) {
    const clean = value.replace(/\D/g, '');
    if (!clean) {
      const next = [...digits];
      next[index] = '';
      setDigits(next);
      return;
    }

    // Beillesztett teljes kód kezelése
    if (clean.length > 1) {
      const next = clean.slice(0, LENGTH).split('');
      const padded = [...next, ...Array(LENGTH - next.length).fill('')];
      setDigits(padded);
      if (padded.join('').length === LENGTH) void verify(padded.join(''));
      return;
    }

    const next = [...digits];
    next[index] = clean;
    setDigits(next);
    if (index < LENGTH - 1) inputs.current[index + 1]?.focus();
    if (next.join('').length === LENGTH) void verify(next.join(''));
  }

  return (
    <div className="otp__backdrop" role="dialog" aria-modal="true" aria-label={t.emailVerification.dialogLabel}>
      <div ref={panelRef} className="otp__sheet">
        <SheetGrip />
        <div className="otp__head">
          <h2 className="otp__title">{t.emailVerification.otp.title}</h2>
          <button type="button" className="otp__close" aria-label={t.common.close} onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="otp__lead">
          {t.emailVerification.otp.leadBeforeEmail}<strong>{address}</strong>{t.emailVerification.otp.leadAfterEmail}
        </p>

        {onChangeEmail && !changing ? (
          <button
            type="button"
            className="auth__link otp__change-link"
            disabled={busy}
            onClick={() => {
              setChanging(true);
              setDraft('');
              setError('');
              setNotice('');
            }}
          >
            {t.emailVerification.otp.wrongAddress}
          </button>
        ) : null}

        {changing ? (
          <form
            className="otp__change"
            onSubmit={(e) => {
              e.preventDefault();
              void changeAddress();
            }}
          >
            <TextField
              label={t.emailVerification.otp.changeLabel}
              hint={t.emailVerification.otp.changeHint}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            {error ? (
              <p className="otp__error" role="alert">
                {error}
              </p>
            ) : null}
            <Button block type="submit" loading={busy} disabled={!draft.trim()}>
              {t.emailVerification.otp.changeSubmit}
            </Button>
            <button
              type="button"
              className="auth__link otp__resend"
              disabled={busy}
              onClick={() => {
                setChanging(false);
                setError('');
              }}
            >
              {t.emailVerification.otp.changeCancel}
            </button>
          </form>
        ) : (
          <>
            <div className="otp__inputs">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputs.current[index] = el;
                  }}
                  className="otp__digit"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={LENGTH}
                  value={digit}
                  aria-label={t.emailVerification.otp.digitLabel(index + 1)}
                  onChange={(e) => setDigit(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !digits[index] && index > 0) {
                      inputs.current[index - 1]?.focus();
                    }
                  }}
                />
              ))}
            </div>

            {error ? (
              <p className="otp__error" role="alert">
                {error}
              </p>
            ) : notice ? (
              <p className="otp__notice">{notice}</p>
            ) : null}

            <Button block loading={busy} disabled={!complete} onClick={() => void verify(code)}>
              {t.emailVerification.otp.verify}
            </Button>

            <button
              type="button"
              className="auth__link otp__resend"
              disabled={wait > 0 || busy}
              onClick={() => void send()}
            >
              {wait > 0 ? t.emailVerification.otp.resendIn(wait) : t.emailVerification.otp.resend}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
